import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  planType: string;
  /** Brand discriminator — 'limephone' (default) | 'limecall' | 'outbound'. Drives infra + theming, not feature access. */
  product?: string;
  status: string;
  lifecycle?: string;
  accessPosture?: string;
  /** Activation review gate: 'pending' holds the app behind the pending page; null/'approved' pass. */
  activationStatus?: string | null;
  trialExpiresAt?: string;
  creditBalance?: string;
  createdAt: string;
  updatedAt: string;
  membership?: {
    id: string;
    role: string;
    status?: string;
    permissions: {
      canManageUsers: boolean;
      canManageSettings: boolean;
      canViewAnalytics: boolean;
      canManagePhoneNumbers: boolean;
      canSendMessages: boolean;
      canCreateCampaigns: boolean;
      canManageTemplates: boolean;
      canAccessCompliance: boolean;
      canManageBilling?: boolean;
    };
    joinedAt: string;
  };
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  isLoading: boolean;
  switchOrganization: (organizationId: string) => Promise<void>;
  createOrganization: (data: {
    name: string;
    slug: string;
    domain?: string;
  }) => Promise<Organization>;
  hasPermission: (permission: keyof NonNullable<Organization['membership']>['permissions']) => boolean;
  refetchOrganizations: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const queryClient = useQueryClient();

  // Check if we're on an auth page to avoid unnecessary API calls
  const isOnAuthPage = window.location.pathname.startsWith('/auth/');
  const hasToken = !!localStorage.getItem('token');

  // Fetch user's organizations
  // Don't fetch if on auth pages to prevent authentication errors
  const { data: organizations = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ['/api/organizations'],
    queryFn: () => apiRequest('GET', '/api/organizations'),
    retry: false,
    refetchInterval: false,
    enabled: hasToken && !isOnAuthPage, // Only fetch if we have a token AND not on auth pages
  });

  // Fetch current organization details
  const { data: currentOrgData, isLoading: isLoadingCurrent } = useQuery({
    queryKey: ['/api/organizations', currentOrganization?.id],
    queryFn: () => apiRequest('GET', `/api/organizations/${currentOrganization?.id}`),
    enabled: !!currentOrganization?.id,
    retry: false,
  });

  // Set initial current organization from localStorage or first organization
  useEffect(() => {
    if (organizations.length > 0 && !currentOrganization) {
      const savedOrgId = localStorage.getItem('currentOrganizationId');
      const targetOrg = savedOrgId 
        ? organizations.find((org: Organization) => org.id === savedOrgId)
        : organizations[0];
      
      if (targetOrg) {
        setCurrentOrganization(targetOrg);
      }
    }
  }, [organizations, currentOrganization]);

  // Update current organization with detailed data
  useEffect(() => {
    if (currentOrgData) {
      setCurrentOrganization(currentOrgData);
    }
  }, [currentOrgData]);

  // Switch organization mutation
  const switchMutation = useMutation({
    mutationFn: async (organizationId: string) => {
      await apiRequest('POST', `/api/organizations/${organizationId}/switch`);
    },
    onSuccess: (_, organizationId) => {
      const newOrg = organizations.find((org: Organization) => org.id === organizationId);
      if (newOrg) {
        setCurrentOrganization(newOrg);
        localStorage.setItem('currentOrganizationId', organizationId);
        // Remove cached API data for the previous org, then refetch org-specific data
        queryClient.removeQueries({ queryKey: ['/api/'], predicate: (query) => {
          const key = query.queryKey[0] as string;
          // Keep auth-related queries intact
          return typeof key === 'string' && key.startsWith('/api/') && !key.startsWith('/api/auth/');
        }});
        queryClient.invalidateQueries({ queryKey: ['/api/organizations'] });
      }
    },
  });

  // Create organization mutation
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; slug: string; domain?: string }) => {
      return await apiRequest('POST', '/api/organizations', data);
    },
    onSuccess: (newOrg) => {
      queryClient.invalidateQueries({ queryKey: ['/api/organizations'] });
      setCurrentOrganization(newOrg);
      localStorage.setItem('currentOrganizationId', newOrg.id);
    },
  });

  const switchOrganization = async (organizationId: string) => {
    await switchMutation.mutateAsync(organizationId);
  };

  const createOrganization = async (data: {
    name: string;
    slug: string;
    domain?: string;
  }): Promise<Organization> => {
    return await createMutation.mutateAsync(data);
  };

  const hasPermission = (permission: keyof NonNullable<Organization['membership']>['permissions']): boolean => {
    if (!currentOrganization?.membership?.permissions) {
      return false;
    }
    return currentOrganization.membership.permissions[permission as keyof typeof currentOrganization.membership.permissions] ?? false;
  };

  const refetchOrganizations = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/organizations'] });
  };

  const value: OrganizationContextType = {
    currentOrganization,
    organizations,
    isLoading: isLoadingOrgs || isLoadingCurrent,
    switchOrganization,
    createOrganization,
    hasPermission,
    refetchOrganizations,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}